import { useEffect, useRef, useState, type CSSProperties } from 'react';
import { ArrowLeftOutlined, ArrowRightOutlined, CheckOutlined } from '@ant-design/icons';
import { useQuery } from '@tanstack/react-query';
import { Alert, Button, Flex, Grid, Modal, Select, Skeleton, Typography, theme } from 'antd';
import {
  CampoDeTelefone,
  Logotipo,
  estiloDeDestaque,
  primeiroNome,
  useColorScheme,
} from '@titularis/ui';
import { api } from '../api';
import { registrarEvento } from '../analytics';
import { authClient, useSession } from '../auth';
import { useAbrirOferta, useCatalogo } from './Oferta';
import { cotaCompartilhada, vantagensDasBoasVindas } from '../boasVindas';

/**
 * As boas-vindas, na primeira entrada.
 *
 * **O buraco que elas fecham.** Até 05/09/2026 o funil terminava no vazio: quem
 * vinha de um link de WhatsApp lia a landing, clicava em "Continuar com Google"
 * e caía direto na vitrine de módulos, sem uma palavra. Sem saber o que a conta
 * gratuita dá e sem nunca ter visto o produto pago - o único sinal de que ele
 * existe era uma faixa de 40px no topo.
 *
 * **Ela é montada pelo `AppLayout`, e não por uma página**, porque a pessoa não
 * aterrissa sempre no mesmo lugar: quem clicou em "Comprar o Enac" na landing
 * chega em `/configuracoes/plano`, e as boas-vindas têm de funcionar lá também.
 *
 * **A pergunta da etapa saiu daqui em 10/09/2026** e virou o telefone. As duas
 * são cadastro, mas só uma é operação: sem telefone não há como falar de uma
 * compra que travou. A preferência de etapa continua existindo e continua em
 * Configurações - quem já respondeu não perde nada, e ninguém é perguntado
 * duas vezes.
 *
 * **Dispensar não é o mesmo que concluir.** Quem chega ao fim grava a data e não
 * vê mais; quem fecha soma uma dispensa e volta a ver, até três vezes - e para
 * de vez assim que responder a primeira questão. Os dois fatos são diferentes e
 * têm colunas diferentes.
 *
 * **A moldura não muda de tamanho entre as etapas.** São 900 × 600 no desktop e
 * uma altura fixa no celular: só o miolo rola. Um modal que encolhe e cresce a
 * cada "Continuar" faz o botão fugir de debaixo do dedo, e a última etapa é
 * justamente a mais alta - a comparação tem uma linha por conteúdo do produto.
 */
const PASSOS = ['A plataforma', 'Seu telefone', 'Grátis e Pro'];

/**
 * Quantas vezes as boas-vindas voltam depois de dispensadas.
 *
 * **Uma vez era pouco** (decisão do Calebe em 05/09/2026): fechar um modal na
 * primeira entrada quer dizer pouca coisa - pode ter sido engano, ou hora ruim,
 * ou o dedo no X procurando outra coisa. Três dá margem para a segunda chance
 * sem virar insistência; cinco atravessaria a fronteira entre lembrar e
 * importunar, e ninguém que ignorou três vezes vai mudar de ideia na quinta.
 *
 * **O número só é seguro por causa da segunda condição**: elas param assim que
 * a pessoa responde a primeira questão. Quem dispensou e foi estudar decidiu.
 */
const MAXIMO_DE_DISPENSAS = 3;

/**
 * A pessoa já fez alguma coisa desde que esta tela carregou?
 *
 * Um gesto só: o primeiro toque, clique ou tecla. Serve para decidir se ainda
 * cabe interromper - e por isso escuta na fase de captura, para saber do gesto
 * mesmo quando ele é atendido por um botão lá embaixo.
 */
function usePrimeiroGesto(): boolean {
  const [houve, setHouve] = useState(false);

  useEffect(() => {
    const marcar = () => setHouve(true);
    const opcoes = { once: true, capture: true } as const;

    window.addEventListener('pointerdown', marcar, opcoes);
    window.addEventListener('keydown', marcar, opcoes);

    return () => {
      window.removeEventListener('pointerdown', marcar, opcoes);
      window.removeEventListener('keydown', marcar, opcoes);
    };
  }, []);

  return houve;
}

/** O que existe para o leitor de tela e não ocupa lugar na tela. */
const SO_PARA_LEITOR: CSSProperties = {
  position: 'absolute',
  width: 1,
  height: 1,
  overflow: 'hidden',
  clipPath: 'inset(50%)',
};

export function BoasVindas() {
  const { token } = theme.useToken();
  const { scheme } = useColorScheme();
  /** O painel da esquerda só cabe quando há duas colunas; no celular ele vira cabeçalho. */
  const comPainel = Boolean(Grid.useBreakpoint().md);
  const { data: sessao } = useSession();
  const usuario = sessao?.user;

  const [passo, setPasso] = useState(0);
  const [fechado, setFechado] = useState(false);
  const [rascunhoDoTelefone, setRascunhoDoTelefone] = useState<string | null>();
  const [telefoneValido, setTelefoneValido] = useState(false);
  const [telefoneCriticavel, setTelefoneCriticavel] = useState(false);
  const [ocupado, setOcupado] = useState(false);
  const [erro, setErro] = useState<string>();
  const [chaveDoProduto, setChaveDoProduto] = useState<string>();
  /*
   * A gravação em voo mora num `useRef`, e não no estado: dois cliques seguidos
   * acontecem antes do repaint, e o estado ainda estaria em `false` no segundo.
   * É o que impede o "Continuar" de gravar duas vezes.
   */
  const emVoo = useRef(false);
  const titulo = useRef<HTMLHeadingElement>(null);
  const miolo = useRef<HTMLDivElement>(null);

  const dispensas = usuario?.onboardingDismissals ?? 0;

  /*
   * Já respondeu alguma questão na vida?
   *
   * É a segunda condição, e é ela que torna o teto de três seguro: quem
   * dispensou e foi estudar **decidiu**, e receber um "Bem-vindo" na terceira
   * sessão seria a plataforma não reparando que a pessoa já chegou.
   */
  const desempenho = useQuery({
    queryKey: ['study-performance'],
    queryFn: () => api.studyPerformance(),
    enabled: Boolean(usuario) && !usuario?.onboardedAt,
  });

  const podeAbrir =
    Boolean(usuario) &&
    !usuario?.onboardedAt &&
    dispensas < MAXIMO_DE_DISPENSAS &&
    Boolean(desempenho.data) &&
    desempenho.data?.total === 0 &&
    !fechado;

  /*
   * **Modal que chega atrasado perdeu a vez.**
   *
   * A decisão de abrir depende do `study-performance`, que responde **depois**
   * de a tela já estar navegável: em 17/09/2026, um clique em "Começar pelo
   * Enac" na Home foi engolido pelas boas-vindas subindo por cima. Quem já
   * está fazendo outra coisa não é interrompido - o convite fica para a
   * próxima visita, e não conta dispensa, porque ele nunca chegou a aparecer.
   *
   * O trinco (ajuste de estado durante o render, como no `auth-guard`) é o que
   * impede o contrário: depois de aberto, o primeiro clique **dentro** do modal
   * não pode fechá-lo.
   */
  const jaInteragiu = usePrimeiroGesto();
  const [entrouEmCena, setEntrouEmCena] = useState(false);
  if (podeAbrir && !jaInteragiu && !entrouEmCena) setEntrouEmCena(true);

  const aberto = podeAbrir && entrouEmCena;

  const status = useQuery({
    queryKey: ['study-status'],
    queryFn: () => api.studyStatus(),
    enabled: aberto,
  });
  const catalogo = useCatalogo();
  const produto = catalogo.data?.find((item) => item.key === chaveDoProduto) ?? catalogo.data?.[0];
  const vantagens = produto ? vantagensDasBoasVindas(produto, status.data?.limites) : [];
  /*
   * O seletor só aparece quando ele diz alguma coisa - a mesma regra da Oferta.
   * Com um produto à venda, ele seria uma caixa de largura inteira com uma
   * opção que não muda nada: ruído com aparência de conteúdo, e ruído caro,
   * porque come a altura que a comparação precisa.
   */
  const haEscolhaDeProduto = (catalogo.data?.length ?? 0) > 1;
  const abrirOferta = useAbrirOferta();

  const telefone = rascunhoDoTelefone === undefined ? usuario?.phone : rascunhoDoTelefone;
  /** Quem já tem número gravado passa sem recriticar o que o cadastro aceitou um dia. */
  const telefoneInalterado = Boolean(telefone) && telefone === usuario?.phone;
  const telefoneReprovado =
    telefoneCriticavel && (!telefone || (!telefoneValido && !telefoneInalterado));

  useEffect(() => {
    if (aberto) registrarEvento({ nome: 'onboarding_shown' });
  }, [aberto]);

  /*
   * Cada etapa começa do começo: o miolo volta ao topo e o foco vai para o
   * título novo. Sem isto, quem rolou a comparação e voltou encontra a etapa
   * anterior no meio, e quem navega por teclado continua no rodapé.
   */
  useEffect(() => {
    if (aberto) {
      miolo.current?.scrollTo(0, 0);
      titulo.current?.focus();
    }
  }, [passo, aberto]);

  function irPara(proximo: number) {
    setErro(undefined);
    setPasso(proximo);
    registrarEvento({ nome: 'onboarding_step', passo: proximo + 1 });
  }

  async function salvarTelefone() {
    setTelefoneCriticavel(true);
    if (!telefone || (!telefoneValido && !telefoneInalterado) || emVoo.current) return;
    /*
     * Nada mudou, nada se grava. Quem chega aqui com o número que já estava no
     * cadastro pagaria uma escrita e um instante de botão aceso para gravar o
     * mesmo texto por cima dele.
     */
    if (telefoneInalterado) {
      irPara(2);
      return;
    }
    emVoo.current = true;
    setOcupado(true);
    setErro(undefined);
    try {
      const resultado = await authClient.updateUser({ phone: telefone });
      if (resultado.error) throw new Error(resultado.error.message);
      irPara(2);
    } catch {
      setErro('Não foi possível salvar seu telefone. Tente novamente.');
    } finally {
      emVoo.current = false;
      setOcupado(false);
    }
  }

  /**
   * Fecha e grava.
   *
   * **Só fecha depois da confirmação da API.** Fechar antes deixaria a pessoa
   * do lado de fora com a conclusão marcada só na tela: na próxima rota o modal
   * voltaria, e o "Começar a estudar" viraria mentira.
   */
  async function encerrar(concluiu: boolean, comOferta = false) {
    if (emVoo.current) return;
    emVoo.current = true;
    setOcupado(true);
    setErro(undefined);
    try {
      const resultado = await authClient.updateUser(
        concluiu ? { onboardedAt: new Date() } : { onboardingDismissals: dispensas + 1 },
      );
      if (resultado.error) throw new Error(resultado.error.message);
      setFechado(true);
      registrarEvento({
        nome: 'onboarding_finished',
        foco: usuario?.focusModules?.join(',') ?? null,
        concluiu,
        dispensas,
      });
      if (comOferta && produto) abrirOferta({ produto: produto.key, origemDaCompra: 'boas-vindas' });
      /*
       * A sessão é relida porque o `AppLayout` decide mostrar isto a partir
       * dela. Falhar aqui não pode transformar uma conclusão já gravada em erro
       * na tela - por isso o engolir do `catch`, e não um `await`.
       */
      void authClient.getSession({ query: { disableCookieCache: true } }).catch(() => undefined);
    } catch {
      setErro('Não foi possível salvar. Tente novamente para continuar.');
    } finally {
      emVoo.current = false;
      setOcupado(false);
    }
  }

  const tituloDoPasso =
    passo === 0
      ? `Bem-vindo${usuario?.name ? `, ${primeiroNome(usuario.name)}` : ''}.`
      : passo === 1
        ? 'Como podemos falar com você?'
        : 'Seu estudo, no seu ritmo.';

  const celula: CSSProperties = {
    padding: token.paddingSM,
    textAlign: 'left',
    verticalAlign: 'top',
    borderBottom: `1px solid ${token.colorBorderSecondary}`,
    /*
     * `break-word`, e não `anywhere`: o segundo parte a palavra no meio mesmo
     * quando ela caberia inteira na linha seguinte - "Registradore/s" no
     * celular, visto no navegador em 10/09/2026.
     */
    overflowWrap: 'break-word',
  };
  /*
   * O cabeçalho da tabela gruda no topo do miolo. Sem isto, "Grátis" e "Pro"
   * saem da tela na segunda linha rolada e as duas colunas ficam sem nome
   * justamente na parte da lista que a pessoa foi procurar.
   */
  const celulaDoTopo: CSSProperties = {
    ...celula,
    position: 'sticky',
    top: 0,
    zIndex: 1,
    background: token.colorBgElevated,
  };

  return (
    <Modal
      open={aberto}
      centered
      width={900}
      footer={null}
      /*
       * Some do DOM ao fechar. Sem isto, as boas-vindas concluídas continuavam
       * ali como um segundo `role=dialog` invisível - dois diálogos no
       * documento, e o leitor de tela lendo os dois.
       */
      destroyOnHidden
      title={<span style={SO_PARA_LEITOR}>Boas-vindas à Titularis</span>}
      onCancel={() => void encerrar(false)}
      closable={{ disabled: ocupado }}
      keyboard={!ocupado}
      mask={{ blur: true, closable: false }}
      afterOpenChange={(visivel) => {
        if (visivel) titulo.current?.focus();
      }}
      /*
       * Sem padding no invólucro, e recortando o que passa das bordas: é o que
       * permite ao painel ocupar a coluna inteira, de borda a borda e do topo
       * ao pé, como na Oferta. Cada coluna passa a pagar o próprio respiro.
       */
      styles={{
        container: { padding: 0, overflow: 'hidden' },
        header: { margin: 0, padding: 0 },
        body: { padding: 0 },
      }}
    >
      {/*
        **A altura é a do passo mais alto, e não um número redondo.** Os 600px
        de antes foram medidos com a comparação listando uma linha por lei;
        agora que ela cabe em três, sobravam quase cem pixels de vazio embaixo
        de cada etapa. Medido em 10/09/2026: o miolo pede 425px na etapa 1, 275
        na 2 e 414 na 3. O que passar disto rola, que é o comportamento para o
        qual a moldura foi feita - fixa para o botão não fugir do dedo, não para
        garantir que tudo caiba.
      */}
      <Flex
        style={{
          height: comPainel ? 'min(540px, calc(100dvh - 48px))' : 'min(620px, calc(100dvh - 32px))',
        }}
      >
        {comPainel && (
          <Flex
            vertical
            justify="space-between"
            style={{
              width: 260,
              flexShrink: 0,
              padding: token.paddingLG,
              overflowY: 'auto',
              /*
               * **No escuro ele fica preto**, e não um cinza mais claro que o
               * modal: o `colorFillTertiary` é um véu branco por cima, então o
               * painel sairia *acima* do miolo na escala de luz - o contrário
               * do claro, onde ele recua. É o mesmo par da Oferta.
               */
              background: scheme === 'dark' ? token.colorBgLayout : token.colorFillTertiary,
            }}
          >
            <Flex vertical gap={token.marginXL}>
              <div style={{ alignSelf: 'flex-start' }}>
                <Logotipo altura={Number(token.fontSizeHeading3)} />
              </div>
              <div>
                <Typography.Title level={2} style={{ ...estiloDeDestaque(32, 300), margin: 0 }}>
                  Conhecimento para a sua próxima etapa.
                </Typography.Title>
                <Typography.Paragraph type="secondary" style={{ marginTop: token.margin }}>
                  Questões e legislação comentada para a preparação de quem estuda para cartórios.
                </Typography.Paragraph>
              </div>
              <Flex vertical gap={token.margin} component="nav" aria-label="Etapas das boas-vindas">
                {PASSOS.map((rotulo, indice) => (
                  <Flex
                    key={rotulo}
                    gap={token.marginSM}
                    align="center"
                    aria-current={passo === indice ? 'step' : undefined}
                    style={{
                      color: passo === indice ? token.colorText : token.colorTextTertiary,
                      fontWeight: passo === indice ? 600 : 400,
                    }}
                  >
                    <span
                      style={{
                        color: token.colorPrimary,
                        minWidth: '2ch',
                        flexShrink: 0,
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {String(indice + 1).padStart(2, '0')}
                    </span>
                    {rotulo}
                  </Flex>
                ))}
              </Flex>
            </Flex>
            <Typography.Text type="secondary" style={{ fontSize: token.fontSizeSM }}>
              Preparação com fundamento.
            </Typography.Text>
          </Flex>
        )}

        <Flex vertical style={{ flex: 1, minWidth: 0, minHeight: 0 }}>
          {/*
            **A contagem de etapas só é escrita onde não há trilha.** No desktop
            ela repetiria em texto o "01 / 02 / 03" do painel ao lado, e a
            moldura tem altura fixa: cada linha aqui é uma linha a menos na
            comparação. Para quem ouve a tela o aviso continua, por uma região
            que só o leitor alcança.
          */}
          {comPainel ? (
            <span aria-live="polite" style={SO_PARA_LEITOR}>
              Etapa {passo + 1} de {PASSOS.length}: {PASSOS[passo]}
            </span>
          ) : (
            <Flex
              vertical
              gap={token.marginXS}
              style={{
                flexShrink: 0,
                padding: token.paddingLG,
                paddingRight: token.paddingXL,
                paddingBottom: 0,
              }}
            >
              <div style={{ alignSelf: 'flex-start' }}>
                <Logotipo altura={Number(token.fontSizeHeading4)} />
              </div>
              <Typography.Text
                type="secondary"
                style={{ fontSize: token.fontSizeSM }}
                aria-live="polite"
              >
                {passo + 1} de {PASSOS.length} · {PASSOS[passo]}
              </Typography.Text>
            </Flex>
          )}

          <div
            ref={miolo}
            style={{
              flex: 1,
              minHeight: 0,
              overflowY: 'auto',
              paddingInline: token.paddingLG,
              /*
               * **O respiro do topo é margem do título, e não padding daqui.**
               * O `top: 0` do cabeçalho grudado mede a partir da caixa de
               * padding do rolador: com padding em cima, ele para 24px abaixo
               * da borda e as linhas já roladas aparecem nessa faixa, por cima
               * dele. Medido no navegador em 10/09/2026.
               */
              paddingBottom: token.padding,
            }}
          >
            <h2
              ref={titulo}
              tabIndex={-1}
              style={{
                /*
                 * Peso 300 e tamanho que cede no celular: é o destaque de toda
                 * tela da casa (Início, notícias, provas). Em 400 ele saía mais
                 * pesado que o "Boa tarde" da Home logo atrás do modal.
                 */
                ...estiloDeDestaque(comPainel ? 32 : 25, 300),
                color: token.colorText,
                // O respiro do topo do miolo, que não pode ser padding do
                // rolador por causa do cabeçalho grudado da comparação.
                marginTop: comPainel ? token.marginLG : token.margin,
                marginBottom: token.margin,
                // O respiro à direita é do botão de fechar, que fica ancorado
                // no canto do modal e não neste bloco.
                paddingInlineEnd: token.paddingXL,
                outline: 'none',
              }}
            >
              {tituloDoPasso}
            </h2>

            {passo === 0 && (
              <Flex vertical gap={token.marginLG}>
                <Typography.Paragraph type="secondary" style={{ margin: 0 }}>
                  Reúna a prática de questões e a leitura da lei em uma rotina de estudo com mais
                  clareza.
                </Typography.Paragraph>
                {[
                  [
                    'Pratique com questões de concurso',
                    'Explore o Enac e os concursos de cartório por prova, disciplina e assunto.',
                  ],
                  [
                    'Entenda o fundamento das respostas',
                    'Consulte resoluções comentadas, com as referências que sustentam cada explicação.',
                  ],
                  [
                    'Aprofunde a leitura da legislação',
                    'Encontre o texto da lei e conheça os comentários disponíveis para cada produto.',
                  ],
                ].map(([rotulo, descricao]) => (
                  <Flex key={rotulo} align="flex-start" gap={token.marginSM}>
                    <CheckOutlined
                      style={{ color: token.colorPrimary, marginTop: token.marginXXS }}
                    />
                    <Flex vertical gap={token.marginXXS}>
                      <Typography.Text strong>{rotulo}</Typography.Text>
                      <Typography.Text type="secondary">{descricao}</Typography.Text>
                    </Flex>
                  </Flex>
                ))}
                <Typography.Text type="secondary" style={{ fontSize: token.fontSizeSM }}>
                  Você pode começar grátis e conhecer os acessos Pro antes de decidir.
                </Typography.Text>
              </Flex>
            )}

            {passo === 1 && (
              /*
               * É um `form` de verdade porque quem digita um telefone termina
               * no Enter, e não no mouse. O botão do rodapé mora fora deste
               * bloco e chega aqui pelo `form=`.
               */
              <form
                id="boas-vindas-telefone"
                onSubmit={(evento) => {
                  evento.preventDefault();
                  void salvarTelefone();
                }}
              >
                {/*
                  **O campo diz para que serve.** Ele era "informe um telefone
                  de contato para o seu cadastro", que é a descrição do ato e
                  não do motivo - e campo sem motivo aparente é campo que se
                  pula: em 17/09/2026, dois de onze cadastros tinham telefone.
                  Continua opcional, e por desenho (D-8): o que se ganha
                  obrigando é número inventado, que é pior que campo vazio
                  porque parece preenchido.
                */}
                <Typography.Paragraph type="secondary">
                  É por onde a gente fala com você se algo travar numa compra ou no seu acesso -
                  e é o único canal que não depende de você abrir o e-mail. Fica opcional, e você
                  pode preencher ou mudar depois, em Configurações.
                </Typography.Paragraph>
                <Flex vertical gap={token.marginSM} style={{ marginBlock: token.marginLG }}>
                  <Typography.Text strong>Telefone com DDD</Typography.Text>
                  <CampoDeTelefone
                    value={telefone}
                    disabled={ocupado}
                    aria-label="Telefone com DDD"
                    status={telefoneReprovado ? 'error' : undefined}
                    onBlur={() => setTelefoneCriticavel(true)}
                    onChange={(valor, valido) => {
                      setRascunhoDoTelefone(valor);
                      setTelefoneValido(valido);
                    }}
                  />
                  {telefoneReprovado && (
                    <Typography.Text type="danger" role="alert">
                      Informe um número completo e válido para o país selecionado.
                    </Typography.Text>
                  )}
                </Flex>
                <Typography.Text type="secondary" style={{ fontSize: token.fontSizeSM }}>
                  Por enquanto, basta informar o número. Não enviaremos um código de verificação
                  nesta etapa.
                </Typography.Text>
              </form>
            )}

            {passo === 2 && (
              <Flex vertical gap={token.margin}>
                <Typography.Paragraph type="secondary" style={{ margin: 0 }}>
                  A conta grátis permite conhecer a plataforma. O Pro amplia o acesso aos conteúdos
                  incluídos no produto escolhido.
                </Typography.Paragraph>
                {catalogo.isPending ? (
                  <Skeleton active paragraph={{ rows: 4 }} />
                ) : catalogo.isError ? (
                  <Alert
                    type="warning"
                    title="Não foi possível carregar os produtos."
                    action={
                      <Button onClick={() => void catalogo.refetch()}>Tentar novamente</Button>
                    }
                  />
                ) : !produto ? (
                  <Alert
                    type="info"
                    title="Nenhum produto Pro está disponível no momento. Você pode continuar com sua conta grátis."
                  />
                ) : (
                  <>
                    {haEscolhaDeProduto && (
                      <Flex vertical gap={token.marginXS}>
                        <Typography.Text strong>Conheça o acesso de cada produto</Typography.Text>
                        <Select
                          aria-label="Produto para comparar"
                          value={produto.key}
                          onChange={setChaveDoProduto}
                          options={catalogo.data?.map((item) => ({
                            value: item.key,
                            label: item.name,
                          }))}
                        />
                      </Flex>
                    )}

                    <table
                      style={{
                        width: '100%',
                        borderCollapse: 'collapse',
                        tableLayout: 'fixed',
                        fontSize: token.fontSizeSM,
                      }}
                    >
                      <caption
                        style={{
                          captionSide: 'top',
                          textAlign: 'left',
                          color: token.colorTextSecondary,
                          paddingBottom: token.paddingXS,
                        }}
                      >
                        O que está incluído em {produto.name}
                      </caption>
                      <thead>
                        <tr>
                          <th scope="col" style={celulaDoTopo}>
                            Recurso
                          </th>
                          <th scope="col" style={celulaDoTopo}>
                            Grátis
                          </th>
                          <th scope="col" style={{ ...celulaDoTopo, color: token.colorPrimary }}>
                            Pro
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {vantagens.map((vantagem) => (
                          <tr key={vantagem.chave}>
                            <th scope="row" style={{ ...celula, fontWeight: 500 }}>
                              {vantagem.rotulo}
                              {vantagem.detalhe && (
                                <div
                                  style={{
                                    fontWeight: 400,
                                    color: token.colorTextTertiary,
                                  }}
                                >
                                  {vantagem.detalhe}
                                </div>
                              )}
                            </th>
                            <td style={celula}>{vantagem.gratis}</td>
                            <td style={{ ...celula, background: token.colorPrimaryBg }}>
                              {vantagem.pro}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    {status.isError && (
                      <Button size="small" onClick={() => void status.refetch()}>
                        Recarregar limites gratuitos
                      </Button>
                    )}
                    <Typography.Text type="secondary" style={{ fontSize: token.fontSizeSM }}>
                      {cotaCompartilhada(produto) &&
                        'As cotas de questões e resoluções são compartilhadas entre os módulos. '}
                      O Pro vale para os conteúdos incluídos neste produto.
                    </Typography.Text>
                  </>
                )}
              </Flex>
            )}
          </div>

          <Flex
            vertical
            gap={token.marginSM}
            style={{
              flexShrink: 0,
              padding: token.paddingLG,
              borderTop: `1px solid ${token.colorBorderSecondary}`,
            }}
          >
            {erro && <Alert type="error" title={erro} showIcon />}
            <Flex justify="space-between" gap={token.marginSM} wrap>
              <Button
                type="text"
                disabled={ocupado}
                onClick={() => (passo > 0 ? irPara(passo - 1) : void encerrar(false))}
                icon={passo > 0 ? <ArrowLeftOutlined /> : undefined}
              >
                {passo > 0 ? 'Voltar' : 'Agora não'}
              </Button>
              {/*
                **Conhecer o Pro não é o botão principal**, e por isso não ocupa
                a largura inteira: em bloco ele era o elemento mais largo da
                tela disputando a atenção com o "Começar a estudar", que é o que
                a etapa pede. Aqui ele é a segunda saída, ao lado da primeira.
              */}
              <Flex gap={token.marginSM} wrap>
                {passo === 2 && produto && !catalogo.isError && (
                  <Button
                    disabled={ocupado}
                    aria-label={`Conhecer o Pro de ${produto.name}`}
                    onClick={() => void encerrar(true, true)}
                  >
                    Conhecer o Pro
                  </Button>
                )}
                <Button
                  type="primary"
                  loading={ocupado}
                  /*
                   * O botão é recriado a cada etapa. Sem a `key`, o clique que
                   * entra na etapa 2 chega ao mesmo elemento já religado ao
                   * `form` do telefone, e submete o formulário que acabou de
                   * nascer.
                   */
                  key={passo}
                  htmlType={passo === 1 ? 'submit' : 'button'}
                  form={passo === 1 ? 'boas-vindas-telefone' : undefined}
                  icon={passo < 2 ? <ArrowRightOutlined /> : undefined}
                  iconPlacement="end"
                  onClick={
                    passo === 1
                      ? undefined
                      : () => {
                          if (passo === 0) irPara(1);
                          else void encerrar(true);
                        }
                  }
                >
                  {passo === 2 ? 'Começar a estudar' : 'Continuar'}
                </Button>
              </Flex>
            </Flex>
          </Flex>
        </Flex>
      </Flex>
    </Modal>
  );
}
